/**
 * Contadores do menu, lado da web (SEN-30). A definição (quais, quem recebe, a frase do leitor de tela) mora em
 * `@sentinela/shared/contagens-menu`; o cálculo, em `GET /api/admin/menu/contagens`. Aqui só: buscar no ritmo certo
 * e colar o número no item certo do registro de telas.
 *
 * Ritmo (sem polling agressivo): busca ao abrir, ao navegar (só se o dado tem mais de 15 s) e a cada 60 s COM A ABA
 * VISÍVEL (`refetchIntervalInBackground: false`: aba escondida não consulta). Uma rota leve para o menu inteiro.
 *
 * Falha da rota = SEM pílula (nunca o último número "congelado" e nunca zero): o menu não afirma o que não leu.
 */
import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CONTAGENS_MENU, descreverContagem, type ContagemMenu, type IdContagemMenu } from "@sentinela/shared/contagens-menu";
import type { ItemNav } from "../components/shell/tipos.js";
import { apiFetch } from "./api.js";

export type ContagensDoMenu = Partial<Record<IdContagemMenu, ContagemMenu | null>>;

export const INTERVALO_CONTAGENS_MS = 60_000;
const VALIDADE_AO_NAVEGAR_MS = 15_000;
const CHAVE = ["menu-contagens"] as const;

export function buscarContagensDoMenu() {
  return apiFetch<{ ok: true; contagens: ContagensDoMenu }>("/api/admin/menu/contagens", { ehAdmin: true });
}

/** Cola o contador no item correspondente do registro. Zero, ausente ou `null` = item sem pílula. */
export function aplicarContagens(itens: readonly ItemNav[], contagens: ContagensDoMenu | undefined): readonly ItemNav[] {
  if (!contagens) return itens;
  const defPorItem = new Map(CONTAGENS_MENU.map((d) => [d.itemId, d] as const));
  return itens.map((item) => {
    const def = defPorItem.get(item.id);
    if (!def) return item;
    const d = descreverContagem(def.id, contagens[def.id]);
    if (!d) return item;
    return { ...item, contador: { valor: d.valor, rotulo: d.rotulo, tom: d.tom, ...(def.destino ? { href: def.destino } : {}) } };
  });
}

/** `habilitado`: só depois do login (sem papel conhecido não há o que perguntar). */
export function useContagensDoMenu(pathname: string, habilitado: boolean): ContagensDoMenu | undefined {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: CHAVE,
    queryFn: buscarContagensDoMenu,
    enabled: habilitado,
    staleTime: VALIDADE_AO_NAVEGAR_MS,
    refetchInterval: INTERVALO_CONTAGENS_MS,
    refetchIntervalInBackground: false,
    retry: 1,
  });
  // Ao navegar: só refaz se já passou da validade (navegar rápido por 5 telas não vira 5 requisições).
  useEffect(() => {
    if (habilitado) void qc.refetchQueries({ queryKey: CHAVE, stale: true });
  }, [pathname, habilitado, qc]);
  return q.isError ? undefined : q.data?.contagens;
}
